/**
 * Codes QR du lien de partage.
 *
 * ## Deux décisions qui conditionnent la lisibilité
 *
 * **Le SVG est construit à la main plutôt que via `renderSVG`.** La fonction de la
 * bibliothèque produit un rectangle par module — 16 Ko de balisage pour un lien typique.
 * En fusionnant les modules sombres contigus de chaque ligne en un seul rectangle, on
 * obtient quelques centaines d'octets. Le rendu est identique, le poids est divisé par
 * plusieurs dizaines.
 *
 * **Les couleurs sont fixes, jamais celles du thème.** Un QR code doit être sombre sur
 * fond clair : c'est ce que suppose la spécification, et un code inversé (clair sur sombre)
 * échoue sur une partie des lecteurs de téléphones. Un joli QR assorti au thème sombre qui
 * ne se scanne pas serait un défaut fonctionnel déguisé en choix esthétique.
 */

import { useMemo } from 'react';
import { encode } from 'uqr';

interface QrCodeProps {
  value: string;
  /** Largeur affichée en pixels. Le SVG reste net à toute taille (vectoriel). */
  size?: number;
  /** Description pour les lecteurs d'écran. */
  label: string;
}

interface ModuleRun {
  x: number;
  y: number;
  width: number;
}

interface QrGeometry {
  runs: ModuleRun[];
  /** Côté de la matrice, zone de silence comprise. */
  extent: number;
}

/**
 * Construit la géométrie du QR : une suite de rectangles horizontaux.
 *
 * `border: 2` ajoute la zone de silence exigée par la spécification. Sans elle, un lecteur
 * peut échouer à distinguer le bord du code de ce qui l'entoure.
 */
export function buildQrGeometry(value: string): QrGeometry {
  const { data, size } = encode(value, {
    // Niveau M : tolère ~15 % de perte. C'est le compromis usuel pour un affichage à
    // l'écran, où l'écran du téléphone peut être légèrement de biais ou sale.
    ecc: 'M',
    border: 2,
  });

  const runs: ModuleRun[] = [];

  for (let y = 0; y < size; y += 1) {
    const row = data[y];
    if (!row) continue;

    let x = 0;
    while (x < size) {
      if (row[x] !== true) {
        x += 1;
        continue;
      }
      // Fusionne les modules sombres contigus : un rectangle au lieu de N.
      let width = 1;
      while (x + width < size && row[x + width] === true) width += 1;
      runs.push({ x, y, width });
      x += width;
    }
  }

  return { runs, extent: size };
}

export function QrCode({ value, size = 208, label }: QrCodeProps): React.JSX.Element {
  const geometry = useMemo(() => buildQrGeometry(value), [value]);

  return (
    <svg
      className="qr-code"
      viewBox={`0 0 ${geometry.extent} ${geometry.extent}`}
      width={size}
      height={size}
      role="img"
      aria-label={label}
      // Sans cela, les bords des modules sont lissés et le contraste local chute :
      // certains lecteurs y perdent la synchronisation.
      shapeRendering="crispEdges"
    >
      <rect width={geometry.extent} height={geometry.extent} fill="#ffffff" />
      <g fill="#14120f">
        {geometry.runs.map((run) => (
          <rect key={`${run.y}-${run.x}`} x={run.x} y={run.y} width={run.width} height={1} />
        ))}
      </g>
    </svg>
  );
}
